import { Id } from '@nextforce/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useServer } from 'app/server/context'
import { produce } from 'immer'
import { Notification } from 'notifications/types'
import { performAction } from 'shared/queries/perform_action'
import keys from './shared/keys'
import { NotificationsData } from './shared/types'

type Variables = {
  notification: Notification
  storeId?: Id
}

export function useMarkNotificationRead() {
  const { server } = useServer()
  const client = useQueryClient()

  return useMutation({
    mutationFn: updateNotification,
    onSuccess: async (_, { notification, storeId }: Variables) => {
      const key = storeId ? keys.list(storeId) : keys.all
      const data = client.getQueryData<NotificationsData>(key)
      if (!data) {
        return
      }

      await client.cancelQueries({ queryKey: key })
      client.setQueryData<NotificationsData>(
        key,
        produce(data, updatedData => {
          for (const page of updatedData.pages) {
            const readNotification = page.notifications.find(
              n => n.id === notification.id
            )

            if (readNotification) {
              readNotification.read = true
              break
            }
          }
        })
      )
    },
    onSettled: async _ => {
      await client.invalidateQueries({ queryKey: keys.summary() })
    },
  })

  async function updateNotification({
    notification,
  }: Variables): Promise<void> {
    await performAction(server, notification.actions?.markAsRead)
  }
}
